import { queryClient } from 'index';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import {
  deleteBuyerModel,
  getBuyerModels,
  toggleOrderAllModels,
} from 'services/BuyerModelsService';
import { LogyxError, getToastErrorMessage } from 'services/api/errors';
import { ReactMutationKeys } from 'services/api/reactMutationKeys';
import { ReactQueryKeys } from 'services/api/reactQueryKeys';

export const useGetBuyerModels = (
  page: number,
  perPage: number,
  searchBy: string,
  buyerId: number,
  sellerId: number
) => {
  return useQuery({
    queryKey: [
      ReactQueryKeys.GET_BUYER_MODELS,
      page,
      perPage,
      searchBy,
      buyerId,
      sellerId,
    ],
    queryFn: () => {
      return getBuyerModels(page, perPage, searchBy, buyerId, sellerId);
    },
    enabled: !!buyerId || !!sellerId,
  });
};

interface IToggleAllModelOrderingForBuyerDTO {
  buyerId: number;
  companyId: number;
  canOrderInternally?: boolean;
  canOrderExternally?: boolean;
}

export const useToggleOrderAllInternallyModels = () => {
  const { t } = useTranslation();
  return useMutation(
    (dto: IToggleAllModelOrderingForBuyerDTO) => {
      return toggleOrderAllModels(
        dto.buyerId,
        dto.companyId,
        dto.canOrderInternally,
        undefined
      );
    },
    {
      onSuccess: (_data, variables) => {
        toast.success(
          variables.canOrderInternally
            ? t('Enabled internal ordering for all models')
            : t('Disabled internal ordering for all models')
        );
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.TOGGLE_ORDER_ALL_MODELS_INTERNALLY,
    }
  );
};

export const useToggleOrderAllExternallyModels = () => {
  const { t } = useTranslation();
  return useMutation(
    (dto: IToggleAllModelOrderingForBuyerDTO) => {
      return toggleOrderAllModels(
        dto.buyerId,
        dto.companyId,
        undefined,
        dto.canOrderExternally
      );
    },
    {
      onSuccess: (_data, variables) => {
        toast.success(
          variables.canOrderExternally
            ? t('Enabled external ordering for all models')
            : t('Disabled external ordering for all models')
        );
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.TOGGLE_ORDER_ALL_MODELS_EXTERNALLY,
    }
  );
};

export const useDeleteBuyerModel = () => {
  const { t } = useTranslation();
  return useMutation(
    (modelId: number) => {
      return deleteBuyerModel(modelId);
    },
    {
      onSuccess: () => {
        toast.success(t('Company access revoked'));
        queryClient.invalidateQueries([ReactQueryKeys.GET_BUYER_MODELS]);
      },
      onError: (error: LogyxError) => {
        toast.error(getToastErrorMessage(error));
      },
      mutationKey: ReactMutationKeys.DELETE_BUYER_MODEL,
    }
  );
};
